import { DealSelectableEntitiesResponseDto } from "@/lib/interfaces/deals";
import { useBreadcrumb } from "@/ui-components/ui/breadcrumb/breadcrumb-context";
import { Button } from "@/ui-components/ui/button";
import DataFetchParent from "@/ui-components/ui/data-fetch";
import { PageTitle, PageTitleContainer } from "@/ui-components/utils/menu-items/page-title";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/ui-components/ui/select";
import { Pencil, PlusIcon } from "lucide-react";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import DealsFetcher from "./fetcher";
import { useUser } from "@/lib/hooks/use-user";
import { UserRole } from "@/lib/interfaces/user";

const Deals = () => {
  const { setTitle } = useBreadcrumb();
  const [searchParams] = useSearchParams();
  const [pageTitle, setPageTitle] = useState("Deals");
  const [selectableEntities, setSelectableEntities] = useState<DealSelectableEntitiesResponseDto>();
  const [selectedPipeline, setSelectedPipeline] = useState<string>(searchParams.get("pipelineId") || "");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (setTitle) {
      setTitle("Deals");
    }
  }, [setTitle]);

  useEffect(() => {
    if (selectableEntities) {
      setIsLoading(false);
    }
  }, [selectableEntities]);

  return (
    <div>
      <PageTitleContainer>
        <div className="flex flex-row justify-between items-center">
          <PageTitle>{pageTitle}</PageTitle>
          <div className="flex flex-row gap-2 items-center">
            <DataFetchParent
              data={selectableEntities}
              isLoading={isLoading}
              renderElement={(response: DealSelectableEntitiesResponseDto) => (
                <SelectableManager
                  selectableEntities={response}
                  selectedPipeline={selectedPipeline}
                  setSelectedPipeline={setSelectedPipeline}
                />
              )}
            />
            <Link to={`/deals/create?pipelineId=${selectedPipeline}`}>
              <Button>+ Neu</Button>
            </Link>
          </div>
        </div>
      </PageTitleContainer>
      <div className="p-4 items-center m-auto">
        <DealsFetcher
          setPageTitle={setPageTitle}
          setSelectableEntities={setSelectableEntities}
          setSelectedPipeline={setSelectedPipeline}
          selectedPipeline={selectedPipeline}
        />
      </div>
    </div>
  );
};

const SelectableManager = ({
  selectableEntities,
  selectedPipeline,
  setSelectedPipeline,
}: {
  selectableEntities: DealSelectableEntitiesResponseDto;
  selectedPipeline: string;
  setSelectedPipeline: Dispatch<SetStateAction<string>>;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useUser();
  
  const onChangePipeline = (value: string) => {
    const params = new URLSearchParams(searchParams);
    params.set("pipelineId", value);
    setSelectedPipeline(value);
    setSearchParams(params);
  };

  return (
    <div>
      <div className="flex flex-row items-center border rounded-md px-1 gap-0.5">
        <Select onValueChange={(value) => onChangePipeline(value)} value={selectedPipeline}>
          <SelectTrigger className="border-none shadow-none gap-2">
            <SelectValue placeholder="Select a pipeline" />
          </SelectTrigger>
          <SelectContent>
            {selectableEntities.pipelines.map((pipeline) => (
              <SelectItem key={pipeline._id} value={pipeline._id}>
                {pipeline.value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(user?.role === UserRole.Admin || user?.role === UserRole.SuperAdmin) && (
          <>
            <Link to={`/pipelines/${selectedPipeline}`}>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <Pencil className="h-4 w-4" />
              </Button>
            </Link>
            <Link to={`/pipelines/create`}>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <PlusIcon className="h-4 w-4" />
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default Deals;
